import React, { useRef } from 'react';
import styled, { css } from 'styled-components';
import { isSiteWithTelBanner, getDevice } from '@panel/commons/helpers';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { sendTrackingInfo } from '@panel/commons';
import OptionsSelector from '@panel/commons/lib/DropDown/OptionSelector';
import { select } from '@panel/commons/core/themev2';
import SimpleTooltip from '@panel/commons/lib/Tooltip/SimpleTooltip';
import EditIcon from '@panel/commons/assets/icons/edicion-grey.svg';
import StatisticsIcon from '@panel/commons/assets/icons/estadisticas-grey.svg';
import StatisticsIconLight from '@panel/commons/assets/icons/estadisticas-greyLight.svg';
import DesempenoIcon from '@panel/commons/assets/icons/desempeno-grey.svg';
import DesempenoIconLight from '@panel/commons/assets/icons/desempeno-greyLight.svg';
import { useSelector } from 'react-redux';
import { bumexFeatureSelectors } from '@panel/core/src/state/ducks/bumexFeatures';
import ExternalIcon from '@panel/commons/assets/icons/external-grey.svg';
import BackIcon from '@panel/commons/assets/icons/flecha-grey.svg';
import PreviewBlack from '@panel/commons/assets/icons/PreviewBlack.svg';
import EditPostingBlack from '@panel/commons/assets/icons/EditPostingBlack.svg';
import ArrowBackBlack from '@panel/commons/assets/icons/arrow-right-black.svg';
import { getLinkToEdit } from '@panel/core/src/utils/getPublisherLinks';
import ButtonShareView from './ButtonShareView';

const ButtonPanel = ({
  isAssistant,
  userIsAuthorized,
  fetchingData,
  isIntegrator,
  setShowModal,
  posting,
  publisherTypeId,
  postingId,
  isStatisticsByPosting,
  onChangeViewPress,
  performanceDisabled,
  showSelectPosting,
  setShowSelectPosting,
  hasSelectorPosting,
  optionsPostingsArray,
  handleOutsideClick,
  publisherId,
  dataToShare,
  setShowShareOptions,
  showShareOptions,
  handleShareButtonOutsideClick,
  setFinishedAction
}) => {
  const history = useHistory();
  const device = getDevice();
  const isDesktop = device === 'desktop';
  const { t } = useTranslation();
  const { urlPosting, publicationType } = posting;
  const positionSelectRef = useRef(null);
  const isDevelopment = publicationType !== 'Clasificado';
  const userIsParticular = publisherTypeId === '1';
  const { bumexFeatures } = useSelector(bumexFeatureSelectors.getBumexFeatureState);

  const onBackButtonPress = () => {
    return history.go(-1);
  };

  const onEditButtonPress = () => {
    sendTrackingInfo('Panel', 'Mis avisos', 'Desempeño del aviso-Editar');
    if (isIntegrator) {
      setShowModal(prev => {
        return {
          ...prev,
          editIntegrator: true
        };
      });
    } else {
      window.location.href = `${window.location.origin}${getLinkToEdit(
        userIsParticular,
        bumexFeatures,
        postingId,
        isDevelopment
      )}`;
    }
  };

  const redirectUrl = `${window.location.origin}/${t('panel.url.property')}/${urlPosting}`;

  const onViewPostingButtonPress = () => {
    sendTrackingInfo('Panel', 'Mis avisos', 'Desempeño del aviso-Ir al aviso');
    window.open(redirectUrl, '_blank');
  };

  const desempenoWording = !performanceDisabled ? 'toDesempeno' : 'withoutDesempeno';

  return (
    <ContentLayout isDesktop={isDesktop} portalWithBanner={isSiteWithTelBanner}>
      {userIsAuthorized && !fetchingData && (
        <SimpleTooltip
          isMobile={!isDesktop}
          position='top-center'
          text={t('panel.desempeno.atras')}
          dataPanel='info-editCard'
        >
          <StyledLink
            icon={isDesktop ? 'backIcon' : 'arrowBackBlack'}
            backSize={`${isDesktop ? 16 : 22}px`}
            onClick={onBackButtonPress}
            isDesktop={isDesktop}
          />
        </SimpleTooltip>
      )}
      <ActionContainer>
        {!isDesktop && !isStatisticsByPosting && (
          <ButtonShareView
            postingId={postingId}
            publisherId={publisherId}
            dataToShare={dataToShare}
            setShowShareOptions={setShowShareOptions}
            showShareOptions={showShareOptions}
            handleShareButtonOutsideClick={handleShareButtonOutsideClick}
            setFinishedAction={setFinishedAction}
          />
        )}
        {!isAssistant && (
          <SimpleTooltip
            isMobile={!isDesktop}
            position='top-center'
            text={t('postings.card.OptionsMenu.linkToEdit')}
            dataPanel='info-editCard'
          >
            <StyledLink
              icon={isDesktop ? 'editIcon' : 'editPostingBlack'}
              className='first-section-button'
              backSize='20px'
              data-panel='panel-buttonPanel-edit'
              onClick={onEditButtonPress}
              isDesktop={isDesktop}
            />
          </SimpleTooltip>
        )}
        <SimpleTooltip
          isMobile={!isDesktop}
          position='top-center'
          text={t('postings.card.OptionsMenu.goToPosting')}
          dataPanel='info-goToPost'
          forceHide={showSelectPosting.toBackButton}
        >
          <StyledLink
            icon={isDesktop ? 'externalIcon' : 'previewBlack'}
            className='first-section-button'
            backSize={`${isDesktop ? 20 : 23}px`}
            onClick={e => {
              e.stopPropagation();
              if (!hasSelectorPosting) onViewPostingButtonPress();
              if (hasSelectorPosting) {
                setShowSelectPosting({ toBackButton: !showSelectPosting.toBackButton, toTitle: false });
              }
            }}
            data-panel='panel-buttonPanel-linkPosting'
            isDesktop={isDesktop}
          />
          {hasSelectorPosting && showSelectPosting.toBackButton && (
            <OptionsSelector
              isOpen={showSelectPosting.toBackButton}
              optionsArray={optionsPostingsArray}
              elementRef={positionSelectRef}
              handleOutsideClick={handleOutsideClick}
              height='94px'
              topDown='60px'
              left={isDesktop ? '30px' : 'unset'}
              right={!isDesktop ? '16px' : 'unset'}
            />
          )}
        </SimpleTooltip>
        {isDesktop && (
          <>
            <Separator />
            <SimpleTooltip
              isMobile={!isDesktop}
              position='top-center'
              text={t(`panel.estadisticas.posting.${isStatisticsByPosting ? desempenoWording : 'toStatistics'}.CTA`)}
              dataPanel='info-editCard'
            >
              <StyledLink
                icon={isStatisticsByPosting ? 'desempenoIcon' : 'statisticsIcon'}
                backSize='20px'
                onClick={!performanceDisabled ? onChangeViewPress : null}
                id='test-btn-1'
                disabled={performanceDisabled}
                isDesktop={isDesktop}
              />
            </SimpleTooltip>
          </>
        )}
      </ActionContainer>
    </ContentLayout>
  );
};

const ContentLayout = styled.div`
  height: 60px;
  width: 100%;
  border-radius: 12px;
  padding: 0 12px 0 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${select('lightGray')};
  margin-bottom: 34px;
  ${({ isDesktop, portalWithBanner }) =>
    !isDesktop &&
    css`
      position: fixed;
      top: ${portalWithBanner ? 89 : 59}px;
      background-color: white;
      box-shadow: 0px 2px 4px 0px #7c98a71a;
      border-radius: 0;
      left: 0;
      z-index: 4;
    `}
`;

const Icons = {
  backIcon: { iconId: BackIcon },
  arrowBackBlack: { iconId: ArrowBackBlack },
  editIcon: { iconId: EditIcon },
  editPostingBlack: { iconId: EditPostingBlack },
  externalIcon: { iconId: ExternalIcon },
  previewBlack: { iconId: PreviewBlack },
  statisticsIcon: { iconId: StatisticsIcon, disabled: StatisticsIconLight },
  desempenoIcon: { iconId: DesempenoIcon, disabled: DesempenoIconLight }
};

const StyledLink = styled.div`
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  background-image: ${({ icon, disabled }) => `url(${!disabled ? Icons[icon].iconId : Icons[icon].disabled})`};
  width: ${({ isDesktop }) => (isDesktop ? '40px' : '48px')};
  height: ${({ isDesktop }) => (isDesktop ? '40px' : '48px')};
  background-repeat: no-repeat;
  background-position: center;
  border-radius: 8px;
  background-size: ${({ backSize }) => backSize};
  position: relative;
  :hover {
    background-color: ${({ disabled, isDesktop }) => {
      if (disabled) return 'none';
      if (isDesktop) return select('midLightGray');
      return select('neutralGray');
    }};
  }
  &.first-section-button {
    margin: 0 4px;
  }
  ${({ display }) =>
    display &&
    css`
      display: ${display};
    `}
`;

const ActionContainer = styled.div`
  display: flex;
  align-items: center;
`;

const Separator = styled.div`
  margin-left: 6px;
  margin-right: 16px;
  width: 1px;
  background-color: ${select('midLightGray')};
  height: 30px;
`;

export default ButtonPanel;
